import { useState, useRef } from "react";
import {
  ChevronDown, ChevronRight, Circle, ImageIcon,
  Loader2, Sparkles, Wand2, X,
} from "lucide-react";
import adminApi from "@/lib/adminApi";

export default function AutoGeneratePanel({
  sortedSlots, artworkUrl, setArtworkUrl, selectedPackId,
  productId, inferredType, onSlotsGenerated,
}) {
  const [autoGenOpen, setAutoGenOpen] = useState(false);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const artworkFileRef = useRef(null);

  // Multi-scene state
  const [multiSceneOpen, setMultiSceneOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [selectedScenes, setSelectedScenes] = useState(new Set());
  const [includeBefore, setIncludeBefore] = useState(false);
  const [multiGenerating, setMultiGenerating] = useState(false);
  const [multiResult, setMultiResult] = useState(null);

  const generatableSlots = sortedSlots.filter(
    (s) => ["detail_crop", "artwork", "room_scene", "mockup_template"].includes(s.source_type)
  );
  const configuredMockups = sortedSlots.filter(
    (s) => s.source_type === "mockup_template" || s.source_type === "room_scene"
  );
  const detailCrops = sortedSlots.filter((s) => s.source_type === "detail_crop");

  const handleArtworkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setArtworkUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", file.name);
      fd.append("category", "presentation");
      fd.append("group", "base-artwork");
      fd.append("status", "published");
      const res = await adminApi.post("/api/admin/media/upload", fd);
      setArtworkUrl(res.data.file_url);
    } catch (err) {
      console.error("[AutoGeneratePanel] Artwork upload failed:", err);
    }
    if (artworkFileRef.current) artworkFileRef.current.value = "";
    setArtworkUploading(false);
  };

  const handleAutoGenerate = async () => {
    if (!artworkUrl.trim() || !selectedPackId || !productId) return;
    setGenerating(true);
    setGenerateResult(null);
    try {
      const res = await adminApi.post(
        `/api/admin/templates/presentations/auto-generate/${productId}`,
        {
          base_artwork_url: artworkUrl.trim(),
          template_pack_id: selectedPackId,
          product_type: inferredType || "",
        }
      );
      const {
        generated,
        errors,
        generated_count,
        presentation_saved,
        completed,
      } = res.data || {};
      onSlotsGenerated(generated || {});

      if (!generated_count) {
        // Surface the first backend error so the user knows why nothing was generated.
        const firstErr = errors && Object.values(errors)[0];
        setGenerateResult({
          success: false,
          message: firstErr || "No images generated. Check that the template pack has eligible slots and the artwork URL resolves.",
          errors: errors || {},
        });
      } else {
        setGenerateResult({
          success: true,
          count: generated_count,
          presentationSaved: !!presentation_saved,
          completed: !!completed,
          errors: errors || {},
        });
      }
    } catch (err) {
      setGenerateResult({
        success: false,
        message: err.response?.data?.detail || err.message || "Generation failed",
        errors: {},
      });
    }
    setGenerating(false);
  };

  const fetchLibraryItems = async () => {
    try {
      const params = new URLSearchParams();
      if (inferredType) params.set("product_type", inferredType);
      const res = await adminApi.get(`/api/admin/templates/mockup-library?${params}`);
      setLibraryItems(res.data.items || []);
    } catch (err) {
      console.error("[AutoGeneratePanel] Failed to load library items:", err);
    }
  };

  const toggleScene = (id) => setSelectedScenes((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleMultiScene = async () => {
    if (!artworkUrl.trim() || selectedScenes.size === 0) return;
    setMultiGenerating(true);
    setMultiResult(null);
    try {
      const res = await adminApi.post("/api/admin/templates/presentations/multi-scene-generate", {
        base_artwork_url: artworkUrl.trim(),
        library_item_ids: [...selectedScenes],
        include_before: includeBefore,
        template_slot_key: "multi_scene",
        product_id: productId || "",
      });
      setMultiResult(res.data);
    } catch (err) {
      setMultiResult({ success: false, error: err.response?.data?.detail || err.message });
    }
    setMultiGenerating(false);
  };

  return (
    <div className="border border-[#CBA052]/25 rounded bg-[#CBA052]/5" data-testid="auto-generate-panel">
      <button
        onClick={() => setAutoGenOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#CBA052]/5 transition-colors"
        data-testid="auto-gen-toggle"
      >
        <Wand2 size={14} className="text-[#CBA052] shrink-0" />
        <span className="text-xs font-bold text-[#CBA052]">Auto-Generate Mockups</span>
        <span className="text-[10px] text-[#CBA052]/50 ml-1">
          {generatableSlots.length}/{sortedSlots.length} slots configured
        </span>
        {autoGenOpen
          ? <ChevronDown size={12} className="text-[#CBA052]/50 ml-auto" />
          : <ChevronRight size={12} className="text-[#CBA052]/50 ml-auto" />}
      </button>

      {autoGenOpen && (
        <div className="border-t border-[#CBA052]/15 p-3 space-y-3">
          {/* Slot readiness summary */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white/[0.03] rounded p-2">
              <p className="text-lg font-bold text-white">{configuredMockups.length}</p>
              <p className="text-[9px] text-white/40 uppercase">Mockup BGs</p>
            </div>
            <div className="bg-white/[0.03] rounded p-2">
              <p className="text-lg font-bold text-white">{detailCrops.length}</p>
              <p className="text-[9px] text-white/40 uppercase">Auto Crops</p>
            </div>
            <div className="bg-white/[0.03] rounded p-2">
              <p className={`text-lg font-bold ${generatableSlots.length > 0 ? "text-[#CBA052]" : "text-white/30"}`}>{generatableSlots.length}</p>
              <p className="text-[9px] text-white/40 uppercase">Ready</p>
            </div>
          </div>

          {generatableSlots.length === 0 && (
            <p className="text-[10px] text-yellow-400/70 bg-yellow-500/10 border border-yellow-500/20 rounded p-2">
              No slots are configured for auto-generation yet. In the Templates tab, edit this pack's slots and add Background URLs to mockup_template slots.
            </p>
          )}

          {/* Slot readiness list */}
          {sortedSlots.length > 0 && (
            <div className="space-y-1">
              {sortedSlots.map((slot) => {
                const isMockup = slot.source_type === "mockup_template";
                const isRoom = slot.source_type === "room_scene";
                const hasBg = (isMockup || isRoom) && (slot.mockup_config?.background_url || "").trim();
                const isCrop = slot.source_type === "detail_crop";
                const isArtwork = slot.source_type === "artwork";
                const canGen = isMockup || isRoom || isCrop || isArtwork;
                return (
                  <div key={slot.key} className="flex items-center gap-2 text-[10px]">
                    {canGen
                      ? <Sparkles size={10} className="text-[#CBA052] shrink-0" />
                      : <Circle size={10} className="text-white/15 shrink-0" />}
                    <span className={canGen ? "text-white/70" : "text-white/25"}>{slot.label}</span>
                    <span className="text-white/20">
                      {(isMockup || isRoom) && hasBg && "(composite)"}
                      {(isMockup || isRoom) && !hasBg && "(auto background)"}
                      {isCrop && "(auto-crop)"}
                      {isArtwork && "(artwork)"}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Base artwork input */}
          <div>
            <label className="text-[9px] text-white/50 uppercase tracking-wider block mb-1.5">Base Artwork</label>
            <div className="flex items-center gap-2">
              <input
                value={artworkUrl}
                onChange={(e) => setArtworkUrl(e.target.value)}
                placeholder="Paste artwork URL or upload below..."
                className="flex-1 bg-white/[0.04] border border-white/[0.08] text-white text-[10px] rounded px-2.5 py-2 outline-none focus:border-[#CBA052]/40"
                data-testid="artwork-url-input"
              />
              <button
                onClick={() => artworkFileRef.current?.click()}
                disabled={artworkUploading}
                className="flex items-center gap-1 text-[10px] font-medium text-white/60 border border-white/15 px-2.5 py-2 rounded hover:text-white hover:border-white/30 disabled:opacity-50 transition-colors shrink-0"
                data-testid="artwork-upload-btn"
              >
                {artworkUploading ? <Loader2 size={10} className="animate-spin" /> : <ImageIcon size={10} />}
                {artworkUploading ? "Uploading..." : "Upload"}
              </button>
              <input ref={artworkFileRef} type="file" accept="image/*" onChange={handleArtworkUpload} className="hidden" />
            </div>
            {artworkUrl && (
              <div className="mt-2 flex items-center gap-2">
                <img src={artworkUrl} alt="Artwork preview" className="w-12 h-12 object-cover rounded border border-white/10 bg-black" />
                <span className="text-[9px] text-white/40 truncate flex-1">{artworkUrl}</span>
                <button onClick={() => setArtworkUrl("")} className="text-white/20 hover:text-red-400"><X size={11} /></button>
              </div>
            )}
          </div>

          {/* Generate button */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleAutoGenerate}
              disabled={generating || !artworkUrl.trim() || generatableSlots.length === 0}
              className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#CBA052] px-4 py-2 rounded hover:opacity-90 disabled:opacity-40 transition-opacity"
              data-testid="generate-all-btn"
            >
              {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
              {generating ? "Generating..." : `Generate ${generatableSlots.length} Slot${generatableSlots.length !== 1 ? "s" : ""}`}
            </button>
            {generateResult && (
              <span
                className={`text-[10px] ${generateResult.success ? "text-green-400" : "text-red-400"}`}
                data-testid="generate-status"
              >
                {generateResult.success
                  ? (() => {
                      const n = generateResult.count;
                      const noun = `${n} image${n !== 1 ? "s" : ""}`;
                      if (generateResult.presentationSaved) {
                        return `${noun} generated and saved${generateResult.completed ? " — presentation complete" : ""}`;
                      }
                      return `${noun} generated — review slots, then Save Presentation`;
                    })()
                  : generateResult.message}
              </span>
            )}
          </div>

          {/* Error detail — surface slot-level errors even on partial success */}
          {generateResult && generateResult.errors && Object.keys(generateResult.errors).length > 0 && (
            <div
              className="text-[9px] text-red-400/70 bg-red-500/10 border border-red-500/20 rounded p-2"
              data-testid="generate-slot-errors"
            >
              <p className="font-bold mb-1">
                {generateResult.success ? "Some slots failed:" : "Generation errors:"}
              </p>
              {Object.entries(generateResult.errors).map(([k, v]) => (
                <p key={k}><span className="font-mono">{k}</span>: {v}</p>
              ))}
            </div>
          )}

          {/* Multi-Scene Generation */}
          <div className="border-t border-white/[0.06] pt-3 mt-2">
            <button
              onClick={() => { setMultiSceneOpen((v) => !v); if (!multiSceneOpen) fetchLibraryItems(); }}
              className="flex items-center gap-1.5 text-[10px] font-bold text-[#CBA052]/70 hover:text-[#CBA052] transition-colors"
              data-testid="multi-scene-toggle"
            >
              {multiSceneOpen ? "Close" : "Multi-Scene Generation"} — use Library backgrounds
            </button>

            {multiSceneOpen && (
              <div className="mt-3 space-y-3">
                {libraryItems.length === 0 ? (
                  <p className="text-[9px] text-white/25">No backgrounds in the library{inferredType ? ` for "${inferredType}"` : ""}. Add some in the Mockup Library section.</p>
                ) : (
                  <div className="grid grid-cols-4 gap-1.5 max-h-32 overflow-y-auto" data-testid="multi-scene-grid">
                    {libraryItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => toggleScene(item.id)}
                        className={`relative rounded overflow-hidden border transition-colors ${
                          selectedScenes.has(item.id)
                            ? "border-[#CBA052]/50 ring-1 ring-[#CBA052]/30"
                            : "border-white/[0.06] hover:border-white/15"
                        }`}
                        data-testid={`scene-pick-${item.id}`}
                      >
                        <div className="aspect-[3/2] bg-black/50">
                          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                        </div>
                        <p className="text-[7px] text-white/50 p-0.5 truncate">{item.name}</p>
                        {selectedScenes.has(item.id) && (
                          <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-[#CBA052] rounded-full flex items-center justify-center">
                            <span className="text-[6px] font-bold text-black">&#10003;</span>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeBefore}
                    onChange={(e) => setIncludeBefore(e.target.checked)}
                    className="w-3.5 h-3.5 accent-[#CBA052] rounded"
                    data-testid="include-before-toggle"
                  />
                  <span className="text-[10px] text-white/50">Include before/after pairs (empty scene + composite)</span>
                </label>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleMultiScene}
                    disabled={multiGenerating || !artworkUrl.trim() || selectedScenes.size === 0}
                    className="flex items-center gap-1.5 text-[10px] font-bold text-black bg-[#CBA052] px-3 py-1.5 rounded hover:opacity-90 disabled:opacity-40"
                    data-testid="multi-scene-generate-btn"
                  >
                    {multiGenerating ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
                    {multiGenerating
                      ? "Generating..."
                      : `Generate ${selectedScenes.size} scene${selectedScenes.size !== 1 ? "s" : ""}${includeBefore ? " (before + after)" : ""}`}
                  </button>
                  <button onClick={() => setSelectedScenes(new Set())} className="text-[9px] text-white/25 hover:text-white">Clear</button>
                </div>

                {multiResult && (
                  <div className={`p-2.5 rounded border text-[10px] ${multiResult.success ? "bg-green-500/10 border-green-500/20" : "bg-red-500/10 border-red-500/20"}`}>
                    {multiResult.success ? (
                      <div className="space-y-1.5">
                        <p className="text-green-400 font-bold">
                          {multiResult.generated_count} scene{multiResult.generated_count !== 1 ? "s" : ""} generated
                          {multiResult.before_after ? " (with before/after)" : ""}
                        </p>
                        {(multiResult.results || []).map((r, i) => (
                          <div key={r.scene_name || r.composite_url || i} className="flex items-center gap-2">
                            <img src={r.composite_url} alt={r.scene_name} className="w-10 h-7 object-cover rounded border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />
                            <span className="text-white/50">{r.scene_name}</span>
                            {r.before_url && <span className="text-white/25">(+ before)</span>}
                          </div>
                        ))}
                        {Object.keys(multiResult.errors || {}).length > 0 && (
                          <div className="text-red-400/70 mt-1">
                            {Object.entries(multiResult.errors).map(([k, v]) => <p key={k}>{v}</p>)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-red-400">{multiResult.error || "Generation failed"}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
